import styled, { css } from "styled-components";
import { IButtonCommonProps, IButtonColorSet, IButtonPreset } from "../button-props.interface";
import {hexToRgba} from "../../../../themes/hex-to-rgba";

interface IButtonBoxProps extends IButtonCommonProps {
    colorSet: IButtonColorSet;

    height: number;
    strokeWidth: number;
    iconSize: number;
    lateralPadding: number;

    hasLabel: boolean;
    hasIcon: boolean;
}
export const ButtonBox = styled.button<IButtonBoxProps>`
    cursor: ${props => props.disabled ? "default" : "pointer"};
    user-select: none;

    border-radius: ${props => props.rounded ? "100px" : props.theme.layout.border.radius};
    ${props => props.margin ? css`
        margin: ${props.margin};
    ` : null};

    /* Override user-agent styles */
    background: none;
    outline: none;

    ${({theme}) => theme.font.presets.ultra};
    /**
     * This is not the actual text size.
     * The text size is set in the span wrapping the text, here i'm just creating a context for the em units to compute relative to this
     */
    font-size: ${props => props.fixedSize ? "10px" : "1rem"};
    /**
     * If we have only an icon and no label, we want a square (or circle) button
     * that means the width = height =>
     * border x 2 + lateral-padding x 2 + icon_size = height =>
     * lateral_padding = (height - icon_size - 2 x border)/2 =>
     * lateral_padding = (height - icon_size)/2 - border
     *
     * If we have label (with or without icon), don't bother, put 1em or whatever value decided by design (hardcoded or from the theme)
     */
    padding: 0 ${props => {
        if (props.hasIcon && !props.hasLabel) {
            return "calc("+((props.height - props.iconSize)/2)+"em - "+props.strokeWidth+"px)";
        } else {
            return props.lateralPadding + "em";
        }
    }};
    line-height: 0.8;
    letter-spacing: 0.05em;

    display: flex;
    align-items: center;
    justify-content: center;

    /** content-box because the border is in px. So when computing heights, paddings etc in em border is not taken in consideration */
    /* box-sizing: content-box;
    height: calc(${props => props.height }em - 2*${props => props.strokeWidth }px); */
    /** OOORRR */
    box-sizing: border-box;
    height: ${props => props.height}em;

    width: ${props => props.fullWidth ? "100%" : "auto"};
    transition: all 0.2s ease-in-out;

    border-width: ${props => props.strokeWidth}px;
    border-style: solid;

    color: ${props => colorSet(props).text};
    background-color: ${props => colorSet(props).bg};
    border-color: ${props => colorSet(props).border};
    box-shadow: ${props => (props.elevated && colorSet(props).shadow) ? colorSet(props).shadow : "none"};

    /* Putting the &:hover rules inside the hover media queries prevents the button to remain in hover state after pressing it.
    We wouldn't need this is the pressed/active state would be different from the hover state */
    @media ${({theme}) => theme.mediaQuery.hover } {
        &:hover {
            color: ${props => colorSet(props, I_WANT_THE_HOVER_COLORS).text};
            background-color: ${props => colorSet(props, I_WANT_THE_HOVER_COLORS).bg};
            border-color: ${props => colorSet(props, I_WANT_THE_HOVER_COLORS).border};
            box-shadow: ${props => (props.elevated && colorSet(props, I_WANT_THE_HOVER_COLORS).shadow) ? colorSet(props, I_WANT_THE_HOVER_COLORS).shadow : "none"};
        }
    }

    ${props => (props.rotated45 && props.hasIcon && !props.hasLabel) ? css`
        transform: rotate(45deg);
    ` : null}
`;

const I_WANT_THE_HOVER_COLORS = true;
function colorSet(props: IButtonBoxProps, hoverSet?: boolean) {
    const setKey: keyof IButtonPreset["colors"] = props.disabled ? "disabled" : (hoverSet || props.active ? "hover" : "normal");
    if (props.colorSet[setKey]) {
        return props.colorSet[setKey];
    } else {
        if (setKey === "disabled") {
            return {
                text: hexToRgba(props.colorSet["normal"].text, 0.8),
                bg: hexToRgba(props.colorSet["normal"].bg, 0.8),
                border: hexToRgba(props.colorSet["normal"].border, 0.8),
                shadow: props.colorSet["normal"].shadow
            };
        }
        if (setKey === "hover") {
            return {
                ...props.colorSet["normal"],
                text: props.colorSet["normal"].bg,
                bg: props.colorSet["normal"].text,
            };
        }
    }
}
